
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCentralizedAuth } from '@/context/CentralizedAuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { hasValidSession, refreshSession } from '@/utils/auth/sessionService';
import { toast } from 'sonner';
import { checkAssessmentAccess } from '@/utils/assessment/assessmentAccess';

export const useAssessmentSession = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useCentralizedAuth();
  const [sessionChecked, setSessionChecked] = useState(false);
  const [activeUserId, setActiveUserId] = useState<string | null>(null);
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  
  // Check if we came from payment success page
  const isFromPaymentSuccess = location.state?.fromPaymentSuccess || false;

  // Load order ID from localStorage
  useEffect(() => {
    const storedOrderId = localStorage.getItem('current_order_id');
    if (storedOrderId) {
      console.log('Found order ID in localStorage:', storedOrderId);
      setCurrentOrderId(storedOrderId);
    } else {
      console.log('No order ID found in localStorage');
    }
  }, []);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        setInitError(null);
        
        if (user?.id) {
          console.log('useAssessmentState: User authenticated via centralized auth with ID:', user.id);
          setActiveUserId(user.id);
          setSessionChecked(true);
          
          // Verify if the user has permission to take the assessment
          const accessCheck = await checkAssessmentAccess(user.id, currentOrderId, isFromPaymentSuccess);
          if (!accessCheck.canAccess) {
            setInitError(accessCheck.message || "You don't have permission to access this assessment");
            // If we need to redirect to payment
            if (accessCheck.requiresPayment) {
              toast.error(accessCheck.message);
              navigate('/');
            }
            return;
          }
          
          // Set the validated order ID
          if (accessCheck.orderId && accessCheck.orderId !== currentOrderId) {
            setCurrentOrderId(accessCheck.orderId);
            localStorage.setItem('current_order_id', accessCheck.orderId);
          }
          
          return;
        }
        
        const { data } = await supabase.auth.getSession();
        
        if (data.session?.user?.id) {
          console.log('useAssessmentState: User authenticated via session with ID:', data.session.user.id);
          setActiveUserId(data.session.user.id);
          setSessionChecked(true);
          
          // Check assessment access
          const accessCheck = await checkAssessmentAccess(data.session.user.id, currentOrderId, isFromPaymentSuccess);
          if (!accessCheck.canAccess) {
            setInitError(accessCheck.message || "You don't have permission to access this assessment");
            return;
          }
          
          if (accessCheck.orderId && accessCheck.orderId !== currentOrderId) {
            setCurrentOrderId(accessCheck.orderId);
            localStorage.setItem('current_order_id', accessCheck.orderId);
          }
          
          return;
        }
        
        const isValidSession = await hasValidSession();
        
        if (isValidSession) {
          const { data: userData } = await supabase.auth.getUser();
          if (userData.user?.id) {
            console.log('useAssessmentState: User authenticated via session validation with ID:', userData.user.id);
            setActiveUserId(userData.user.id);
            setSessionChecked(true);
            
            // Check assessment access
            const accessCheck = await checkAssessmentAccess(userData.user.id, currentOrderId, isFromPaymentSuccess);
            if (!accessCheck.canAccess) {
              setInitError(accessCheck.message || "You don't have permission to access this assessment");
              return;
            }
            
            if (accessCheck.orderId && accessCheck.orderId !== currentOrderId) {
              setCurrentOrderId(accessCheck.orderId);
              localStorage.setItem('current_order_id', accessCheck.orderId);
            }
            
            return;
          }
        }
        
        console.log('useAssessmentState: Attempting to refresh session');
        const refreshed = await refreshSession();
        
        if (refreshed) {
          const { data: refreshData } = await supabase.auth.getSession();
          
          if (refreshData.session?.user?.id) {
            console.log('useAssessmentState: User authenticated after session refresh with ID:', refreshData.session.user.id);
            setActiveUserId(refreshData.session.user.id);
            setSessionChecked(true);
            
            // Check assessment access
            const accessCheck = await checkAssessmentAccess(refreshData.session.user.id, currentOrderId, isFromPaymentSuccess);
            if (!accessCheck.canAccess) {
              setInitError(accessCheck.message || "You don't have permission to access this assessment");
              return;
            }
            
            if (accessCheck.orderId && accessCheck.orderId !== currentOrderId) {
              setCurrentOrderId(accessCheck.orderId);
              localStorage.setItem('current_order_id', accessCheck.orderId);
            }
            
            return;
          }
        }
        
        // If we reach here, the user is not authenticated
        setInitError("Please log in to access the assessment");
        navigate('/login', { state: { returnUrl: '/assessment' } });
        
      } catch (error) {
        console.error('Error checking auth:', error);
        setInitError('Authentication error. Please try logging in again.');
        setSessionChecked(true);
      } finally {
        setIsInitializing(false);
      }
    };

    checkAuth();
  }, [user, currentOrderId, navigate, isFromPaymentSuccess]);

  return {
    sessionChecked,
    activeUserId,
    currentOrderId,
    initError,
    isInitializing,
    setIsInitializing
  };
};
